/** One public feature catalogue for the platform menu and feature navigation. */
export type PublicPlatformFeature = {
  key: string;
  domain: "ai" | "care" | "attention" | "planning" | "society" | "human";
  label: Record<"sv" | "en", string>;
  description: Record<"sv" | "en", string>;
  paths: Record<"sv" | "en", string>;
};

export const PUBLIC_PLATFORM_FEATURES: PublicPlatformFeature[] = [
  {
    key: "ai-scheduling",
    domain: "ai",
    label: { sv: "AI-schemaläggning", en: "AI scheduling" },
    description: {
      sv: "Planera, optimera, granska, publicera och lär av utfallet.",
      en: "Plan, optimize, review, publish, and learn from outcomes.",
    },
    paths: {
      sv: "/produkt/funktioner/ai-schemalaggning",
      en: "/en/product/features/ai-scheduling",
    },
  },
  {
    key: "mobile-apps",
    domain: "care",
    label: { sv: "Mobilappar", en: "Mobile apps" },
    description: {
      sv: "Vårdgivare, klienter och anhöriga i samma operativa flöde.",
      en: "Caregivers, clients, and family in one operating flow.",
    },
    paths: {
      sv: "/produkt/funktioner/mobilappar",
      en: "/en/product/features/mobile-apps",
    },
  },
  {
    key: "handbook",
    domain: "care",
    label: { sv: "Personalhandbok", en: "Employee handbook" },
    description: {
      sv: "Rutiner, omsorgsmallar och introduktion nära arbetet.",
      en: "Routines, care templates and onboarding close to daily work.",
    },
    paths: {
      sv: "/produkt/funktioner/personalhandbok",
      en: "/en/product/features/employee-handbook",
    },
  },
  {
    key: "analytics",
    domain: "attention",
    label: { sv: "Analysverktyg", en: "Analytics" },
    description: {
      sv: "Följ kvalitet, kapacitet, ekonomi och förbättring över tid.",
      en: "Track quality, capacity, economics, and improvement over time.",
    },
    paths: {
      sv: "/produkt/funktioner/analysverktyg",
      en: "/en/product/features/analytics",
    },
  },
  {
    key: "care-plans",
    domain: "care",
    label: { sv: "Genomförandeplan", en: "Care plans" },
    description: {
      sv: "Besök, tidsfönster och insatsflex som schemat följer.",
      en: "Visits, time windows and visit flex the schedule follows.",
    },
    paths: {
      sv: "/produkt/funktioner/genomforandeplan-hemtjanst",
      en: "/en/product/features/care-plans-home-care",
    },
  },
  {
    key: "compare-proposals",
    domain: "attention",
    label: { sv: "Jämför schemaförslag", en: "Compare proposals" },
    description: {
      sv: "Se förslagen sida vid sida och välj innan ni publicerar.",
      en: "See proposals side by side and choose before you publish.",
    },
    paths: {
      sv: "/produkt/funktioner/jamfor-schemaforslag",
      en: "/en/product/features/compare-schedule-proposals",
    },
  },
  {
    key: "route-optimization",
    domain: "planning",
    label: { sv: "Ruttoptimering", en: "Route optimization" },
    description: {
      sv: "Mindre restid, bättre kontinuitet och tydligare ruttbeslut.",
      en: "Less travel, better continuity, and clearer routing decisions.",
    },
    paths: {
      sv: "/produkt/funktioner/ruttoptimering-hemtjanst",
      en: "/en/product/features/route-optimization-home-care",
    },
  },
  {
    key: "ai-chat",
    domain: "ai",
    label: { sv: "AI-chat", en: "AI chat" },
    description: {
      sv: "Operativ assistans för planering och uppföljning.",
      en: "Operational assistance for planning and follow-up.",
    },
    paths: {
      sv: "/produkt/funktioner/ai-chat",
      en: "/en/product/features/ai-chat",
    },
  },
  {
    key: "ai-staff-planning",
    domain: "planning",
    label: {
      sv: "AI-personalplanering",
      en: "AI staff planning",
    },
    description: {
      sv: "Behov, kompetens och preferenser i personalplaneringen.",
      en: "Demand, skills, and preferences in staff planning.",
    },
    paths: {
      sv: "/produkt/funktioner/ai-personalplanering-hemtjanst",
      en: "/en/product/features/ai-staff-planning-home-care",
    },
  },
  {
    key: "security-compliance",
    domain: "society",
    label: {
      sv: "Säkerhet & efterlevnad",
      en: "Security & compliance",
    },
    description: {
      sv: "Behörigheter, spårbarhet, dataskydd och kontroller.",
      en: "Permissions, traceability, data protection, and controls.",
    },
    paths: {
      sv: "/produkt/funktioner/sakerhet-efterlevnad",
      en: "/en/product/features/security-compliance",
    },
  },
  {
    key: "onboarding",
    domain: "human",
    label: { sv: "Onboarding", en: "Onboarding" },
    description: {
      sv: "Införande, utbildning och trygg produktstart.",
      en: "Rollout, training, and a controlled product launch.",
    },
    paths: {
      sv: "/produkt/funktioner/onboarding",
      en: "/en/product/features/onboarding",
    },
  },
  {
    key: "reports",
    domain: "attention",
    label: { sv: "Rapporter", en: "Reports" },
    description: {
      sv: "Källor, mått och plan mot utfall.",
      en: "Sources, measures and plan against actuals.",
    },
    paths: {
      sv: "/produkt/funktioner/rapporter",
      en: "/en/product/features/reports",
    },
  },
  {
    key: "integrations",
    domain: "society",
    label: { sv: "Integrationer", en: "Integrations" },
    description: {
      sv: "Anslut befintliga system och välj rätt dataflöde.",
      en: "Connect existing systems and choose the right data flow.",
    },
    paths: { sv: "/plattform/integrationer", en: "/en/platform/integrations" },
  },
];
